import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChatView, ChatViewProps } from './ChatView';
import { useSearchPolicyTransactions } from '@services/policy';
import { useSearchMessages } from '@services/note/messages/useSearchMessages';
import { PolicyTransactionTypes, PolicyTransactionTypesToLabel } from '@models/policyTransaction';

export type ChatViewWrapperProps = {
  isOpen: boolean;
  isSearchMode: boolean;
  anchorId: string | null;

  policyId?: string;
  quoteId?: string;

  chatViewPropsConfig?: ChatViewProps['config'];
};

export const ChatViewWrapper = ({
  chatViewPropsConfig,
  policyId,
  quoteId,
  isOpen,
  isSearchMode,
  anchorId,
}: ChatViewWrapperProps) => {
  const [draggedFiles, setDraggedFiles] = useState<File[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [initialScrollToBottom, setInitialScrollToBottom] = useState(false);
  // Scroll management refs
  const hasInitialScrollRef = useRef(false);
  const userNearBottomRef = useRef(true);
  const lastMessageIdRef = useRef<string | undefined>(undefined);

  const { policyTransactions } = useSearchPolicyTransactions({ policyId: policyId ?? '' }, !!policyId);

  const {
    messages: apiMessages,
    loadOlder,
    loadNewer,
    isLoadingOlder,
    isLoadingNewer,
    isLoading,
    lastPage,
    firstPage,
  } = useSearchMessages({
    policyId,
    quoteId,
    params: {
      beforeLimit: anchorId ? 10 : 20,
      afterLimit: anchorId ? 10 : 20,
      anchorId: anchorId ?? undefined,
    },
    enabled: true,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      // Only poll if there are not known newer
      if (!lastPage?.anchor?.hasNewer) {
        loadNewer();
      }
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [lastPage, loadNewer]);

  // Initial scroll to bottom when dialog opens & messages first arrive
  useEffect(() => {
    if (!isOpen || isSearchMode) return;
    if (hasInitialScrollRef.current) return;
    if (!apiMessages.length) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
        hasInitialScrollRef.current = true;
        userNearBottomRef.current = true;
        lastMessageIdRef.current = apiMessages[apiMessages.length - 1]?.id;
      });
    });
  }, [apiMessages, apiMessages.length, isOpen, isSearchMode]);

  const handleScrollToBottom = useCallback(
    (triggerTwice: boolean = true) => {
      const el = scrollRef.current;
      if (!el) return;
      let tries = 0;
      const max = 8;
      const tick = () => {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        const gap = el.scrollHeight - (el.scrollTop + el.clientHeight);
        if (gap > 2 && tries < max) {
          tries += 1;
          requestAnimationFrame(tick);
        }

        if (triggerTwice) {
          // trigger twice to ensure we are at the bottom
          setTimeout(() => {
            handleScrollToBottom(false);
          }, 300);
        } else if (!initialScrollToBottom) {
          setInitialScrollToBottom(true);
        }
      };
      requestAnimationFrame(tick);
    },
    [initialScrollToBottom],
  );

  // Auto-scroll to bottom on first modal load only
  useEffect(() => {
    if (apiMessages.length && !initialScrollToBottom) {
      setTimeout(() => {
        // initial scroll will do some of it
        handleScrollToBottom();
      }, 200);
    }
  }, [isOpen, apiMessages, apiMessages.length, handleScrollToBottom, initialScrollToBottom]);

  useEffect(() => {
    setInitialScrollToBottom(false);
  }, [isOpen]);

  // Scroll position preservation when prepending older messages
  const prevScrollHeightRef = useRef<number | null>(null);
  useEffect(() => {
    if (isLoadingOlder) {
      if (scrollRef.current) prevScrollHeightRef.current = scrollRef.current.scrollHeight;
    } else if (!isLoadingOlder && prevScrollHeightRef.current !== null && scrollRef.current) {
      const newHeight = scrollRef.current.scrollHeight;
      const diff = newHeight - prevScrollHeightRef.current;
      scrollRef.current.scrollTop = diff + scrollRef.current.scrollTop;
      prevScrollHeightRef.current = null;
    }
  }, [isLoadingOlder]);

  // Track if user is near the bottom (so we only auto-scroll when appropriate)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handle = () => {
      const gap = el.scrollHeight - (el.scrollTop + el.clientHeight);
      // consider within 48px of bottom as near-bottom
      userNearBottomRef.current = gap < 48;
    };
    handle(); // initialize
    el.addEventListener('scroll', handle, { passive: true });
    return () => el.removeEventListener('scroll', handle);
  }, []);

  // When new messages arrive (polling/interval inside useSearchMessages) auto-scroll if already at bottom
  useEffect(() => {
    if (isSearchMode) return; // do not interfere with search mode
    if (!apiMessages.length) return;
    const latestId = apiMessages[apiMessages.length - 1]?.id;
    const previousLastId = lastMessageIdRef.current;
    // Only act if we already had a last id (not the very first load) and it changed (a new message appended)
    if (previousLastId && previousLastId !== latestId && userNearBottomRef.current && initialScrollToBottom) {
      // Scroll smoothly without the extra recursive attempts (content already measured by virtualization)
      handleScrollToBottom(false);
    }
    lastMessageIdRef.current = latestId;
  }, [apiMessages, isSearchMode, handleScrollToBottom, initialScrollToBottom]);

  const events = useMemo(() => {
    const events: {
      id: string;
      date: Date;
      text: string;
    }[] = [];
    policyTransactions?.items.forEach((t) => {
      if (t.transactionType !== PolicyTransactionTypes.NewBusiness) {
        events.push({
          id: `${t.id}-created`,
          date: new Date(t.createdAt),
          text: `an ${PolicyTransactionTypesToLabel[t.transactionType as any]} was created`,
        });
      }
      if (t.processedOn) {
        events.push({
          id: `${t.id}-bound`,
          date: new Date(t.processedOn),
          text: `an ${PolicyTransactionTypesToLabel[t.transactionType as any]} was bound`,
        });
      }
    });

    return events;
  }, [policyTransactions?.items]);

  return (
    <ChatView
      showEmptyList={!isLoading && !isSearchMode && apiMessages?.length === 0}
      isLoadingInitialView={isLoading}
      isHidden={isSearchMode}
      messages={apiMessages}
      scrollRef={scrollRef}
      loadOlder={loadOlder}
      loadNewer={loadNewer}
      hasOlder={firstPage?.anchor.hasOlder}
      hasNewer={lastPage?.anchor?.hasNewer}
      isLoadingOlder={isLoadingOlder}
      isLoadingNewer={isLoadingNewer}
      anchorId={anchorId}
      initialScrollToBottom={initialScrollToBottom}
      onSendNewMessage={handleScrollToBottom}
      policyId={policyId}
      quoteId={quoteId}
      draggedFiles={draggedFiles}
      setDraggedFiles={setDraggedFiles}
      events={events}
      config={chatViewPropsConfig}
    />
  );
};
